import React, { useState, useRef, useEffect } from 'react';
import { Social, useSocial } from '../context/socialProvider';
import logo from '../assets/logo.png';
import {
  ViewColumnsIcon,
  ArrowRightIcon,
  Cog8ToothIcon,
  UserCircleIcon,
  PlusIcon, // Add PlusIcon here
} from '@heroicons/react/24/outline';
import { useLocation, useParams } from 'react-router-dom';

import InstagramIcon from '../assets/instagram.svg';
import FacebookIcon from '../assets/facebook.svg';
import LinkedinIcon from '../assets/linkedin.svg';
import TwitterIcon from '../assets/twitter.svg';
import TikTokIcon from '../assets/tiktok.svg';
import { Link, useNavigate } from 'react-router-dom';
import { isSettingsPage } from '../utils/navigation';
import PlatformSkeleton from './SidebarSkeleton';

const platformIcons = {
  1: InstagramIcon, // Instagram
  2: FacebookIcon, // Facebook
  3: LinkedinIcon, // LinkedIn
  4: TikTokIcon, // TikTok
  5: TwitterIcon, // X/Twitter
};

const Sidebar = () => {
  const { availableSocials, currentSocial, switchSocial, isLoading } =
    useSocial();
  const [expanded, setExpanded] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { providerId, providerName } = useParams<{
    providerId: string;
    providerName: string;
  }>();

  useEffect(() => {
    if (
      providerId &&
      providerName &&
      providerName !== 'global' &&
      (!currentSocial ||
        currentSocial.id.toString() !== providerId ||
        currentSocial.providerName !== providerName)
    ) {
      // Find the social with matching providerId and providerName
      const matchingSocial = availableSocials.find(
        (social) =>
          social.id.toString() === providerId &&
          (social.providerName === providerName ||
            encodeURIComponent(social.providerName) === providerName)
      );

      // If found, switch to it
      if (matchingSocial) {
        switchSocial(matchingSocial.id);
      }
    }
  }, [providerId, providerName, currentSocial, availableSocials, switchSocial]);
  // Filter socials based on search term and hide provider 3 (LinkedIn)
  const filteredSocials = availableSocials
    .filter((social) => social.provider !== 3)
    .filter((social) =>
      social.providerName.toLowerCase().includes(searchTerm.toLowerCase())
    );

  // Sort socials alphabetically by platform type then by name
  const sortedSocials = [...filteredSocials].sort((a, b) => {
    // First by platform type
    if (a.provider !== b.provider) {
      return a.provider - b.provider;
    }
    // Then alphabetically by name
    return a.providerName.localeCompare(b.providerName);
  });

  // Group socials by platform
  const socialsByPlatform: { [provider: number]: Social[] } = {};
  sortedSocials.forEach((social) => {
    if (!socialsByPlatform[social.provider]) {
      socialsByPlatform[social.provider] = [];
    }
    socialsByPlatform[social.provider].push(social);
  });

  // Platform names for headings
  const platformNames: { [provider: number]: string } = {
    1: 'Instagram',
    2: 'Facebook',
    3: 'LinkedIn', // This will be filtered out but good to keep for completeness
    4: 'TikTok',
    5: 'Twitter',
  };

  const setLastPage = (page: string) => {
    localStorage.setItem('lastPage_global', page);
  };

  const getLastPage = () => {
    return localStorage.getItem('lastPage_global') || 'postsview';
  };

  // Save last visited page (section) globally on route change
  useEffect(() => {
    const match = location.pathname.match(/^\/[^/]+\/[^/]+\/([^/]+)/);
    if (match && match[1]) {
      setLastPage(match[1]);
    }
  }, [location.pathname]);

  // Determine if a social item is active
  const isSocialItemActive = (social: Social) => {
    // If we're on a settings page
    if (location.pathname.includes('/settings')) {
      // Check if this social's ID and name match the provider in the URL
      return (
        location.pathname.includes(
          `/${social.id}/${encodeURIComponent(social.providerName)}/settings`
        ) ||
        location.pathname.includes(
          `/${social.id}/${social.providerName}/settings`
        )
      );
    }

    // For non-settings pages, check if this matches the current social
    return social.id === currentSocial?.id;
  };

  return (
    <aside className="h-screen flex">
      <nav className="h-full flex flex-col bg-grey border-r-1 border-white border-opacity-20">
        <div className="p-4 pt-7 pb-5 h-24 flex justify-between items-center border-b-1 border-white border-opacity-20">
          <img
            src={logo}
            className={`overflow-hidden transition-all cursor-pointer mt-2 ${
              expanded ? 'w-32' : 'w-0'
            }`}
            alt="Logo"
          />
          <button
            onClick={() => setExpanded((status) => !status)}
            className="p-1.5 rounded-lg bg-grey-50 hover:bg-grey-100"
          >
            {expanded ? (
              <ViewColumnsIcon className="h-6 w-6" />
            ) : (
              <ArrowRightIcon className="h-6 w-6" />
            )}
          </button>
        </div>

        {/* Social accounts list */}
        <div className="flex-grow overflow-y-auto">
          <ul className="py-2">
            {/* Add Account Button */}
            <li>
              <Link
                to="/onboarding"
                className={`flex items-center w-full px-3 py-2 text-white text-opacity-90 hover:bg-white hover:bg-opacity-10 transition-colors ${
                  expanded ? '' : 'justify-center'
                }`}
              >
                <PlusIcon className="w-6 h-6 flex-shrink-0" />
                {expanded && (
                  <div className="ml-3 overflow-hidden">
                    <div className="text-sm font-medium">Add Account(s)</div>
                  </div>
                )}
              </Link>
            </li>

            {isLoading ? (
              <>
                {/* Show skeleton for multiple platforms */}
                <PlatformSkeleton expanded={expanded} />
                <PlatformSkeleton expanded={expanded} />
              </>
            ) : (
              /* Actual social accounts */
              Object.entries(socialsByPlatform).map(([provider, socials]) => (
                <React.Fragment key={provider}>
                  <li
                    className={`px-3 py-1 text-xs font-semibold text-gray-400 uppercase tracking-wide ${
                      expanded ? '' : 'text-center'
                    }`}
                  >
                    {expanded
                      ? platformNames[Number(provider)]
                      : platformNames[Number(provider)]?.charAt(0)}
                  </li>
                  {socials.map((social) => {
                    const isActive = isSocialItemActive(social);
                    return (
                      <li key={social.id}>
                        <button
                          onClick={() => {
                            switchSocial(social.id);
                            const lastPage = getLastPage();
                            // Create URL with both ID and name
                            navigate(
                              `/${social.id}/${encodeURIComponent(
                                social.providerName
                              )}/${lastPage}`
                            );
                          }}
                          className={`flex items-center w-full px-3 py-2 ${
                            isActive
                              ? 'bg-violet'
                              : 'text-white text-opacity-90 hover:bg-white hover:bg-opacity-10'
                          } 
                        ${expanded ? '' : 'justify-center'}
                        transition-colors`}
                        >
                          {/* Profile picture with platform logo overlay */}
                          <div className="w-8 h-8 relative flex-shrink-0">
                            {social.profilePictureUrl ? (
                              <img
                                src={social.profilePictureUrl}
                                alt={`${social.providerName} profile`}
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  target.src =
                                    'https://superpower-alpha.s3.amazonaws.com/default_images/no_pfp.jpg';
                                }}
                                className="w-8 h-8 rounded-full object-cover border border-black border-opacity-20"
                              />
                            ) : (
                              <UserCircleIcon className="w-8 h-8 text-gray-400" />
                            )}
                            <img
                              src={
                                platformIcons[
                                  social.provider as keyof typeof platformIcons
                                ]
                              }
                              alt={`${
                                platformNames[
                                  social.provider as keyof typeof platformNames
                                ]
                              } logo`}
                              className={`w-4 h-4 absolute bottom-0 right-0 rounded-full border-2 bg-black ${
                                isActive ? 'border-violet' : 'border-grey'
                              }`}
                              style={{ backgroundColor: 'black' }}
                            />
                          </div>
                          {expanded && (
                            <div className="ml-3 overflow-hidden">
                              <div className="text-sm font-medium truncate">
                                {social.providerName}
                              </div>
                            </div>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </React.Fragment>
              ))
            )}
          </ul>
        </div>

        <div className="border-t border-black border-opacity-20 py-1">
          <Link
            to="/global/settings"
            onClick={() => switchSocial(0)} // Deselect current social
            className={`flex items-center py-2 px-3 font-medium cursor-pointer text-nowrap transition-colors
          ${expanded ? 'justify-start' : 'justify-center'}
          ${
            location.pathname === '/global/settings'
              ? 'bg-violet'
              : 'hover:bg-violet'
          }
        `}
          >
            <Cog8ToothIcon className="w-6 h-6" />
            {expanded && <span className="ml-3">Settings</span>}
          </Link>
        </div>
      </nav>
    </aside>
  );
};

export default Sidebar;
